import axios from 'axios'
import { useToast } from 'vue-toastification';


const toast = useToast();

const getBookmarkedSensor = (state, sensorId) => {
  return state.bookmarkedSensors.find(
    (sensor) => sensor.sensor_id === sensorId
  );
};

export const user = {
  namespaced: true,
  state: {
    url_list: 'users',
    url_users_for_group: 'users/group',
    url_user: 'user/info',
    url_profile: 'user/profile',
    url_add_users_to_group: 'group/add_users/',
    current_user_id: 0,
    url_remove_user_from_group: 'user/remove_group/',
    url_upsert_attribute: 'upsert_attribute',
    url_notification_test: 'test/',
    url_invite: 'user/invite/',
    user_info: [],
    invite_email: '',
    invite_user_modal_shown: false,
    invite_lock_status: undefined,
    bookmarkedSensors: [],
    lastReadings: [],
    lastReadingsLoading: false,
    bookmarkedReadings: [],
    bookmarkedReadingsLoading: false,
    communicationCounts: [],
    // Caching state
    cache: {
      bookmarkedReadings: null,
      lastReadings: null,
      communicationCounts: null,
      lastCacheTime: {
        bookmarkedReadings: 0,
        lastReadings: 0,
        communicationCounts: 0,
      },
    },
    // Cache configuration (in milliseconds)
    cacheTimeout: 5 * 60 * 1000, // 5 minutes
  },

  mutations: {
    setBookmarkedReadingsLoading(state, payload) {
      state.bookmarkedReadingsLoading = payload;
    },
    setLastReadingsLoading(state, payload) {
      state.lastReadingsLoading = payload;
    },
    toggleAddUserModal(state, payload) {
      state.invite_email = '';
      state.invite_user_modal_shown = payload;
    },
    setInviteEmail(state, payload) {
      state.invite_email = payload;
    },
    setBookmarks(state, payload) {
      // Normalize payload to ensure sensor_id exists and deleted is a boolean
      const normalized = Array.isArray(payload) ? payload.map(item => ({
        ...item,
        sensor_id: item.sensor_id || item.sensorid || item.id,
        deleted: item.deleted === true || item.deleted === 1 || item.deleted === 'true'
      })) : [];
      
      state.bookmarkedSensors = normalized;
      state.bookmarkedReadings = normalized;
    },
    deleteBookmark(state, sensorId) {
      let bookmark = getBookmarkedSensor(state, sensorId);
      if (bookmark != null) bookmark.deleted = true;
      
      // Also update bookmarkedReadings to keep them in sync
      let reading = state.bookmarkedReadings.find(r => r.sensor_id === sensorId);
      if (reading) reading.deleted = true;
    },
    addBookmark(state, sensorId) {
      let bookmark = getBookmarkedSensor(state, sensorId);
      if (bookmark) {
        if (bookmark.deleted) {
          bookmark.deleted = false;
        }
        return;
      }
      // only add if not already bookmarked
      const newBookmark = {
        sensor_id: sensorId,
        deleted: false,
      };
      state.bookmarkedSensors.push(newBookmark);
      state.bookmarkedReadings.push(newBookmark);
    },
  },
  getters: {
    bookmarkedSensors: (state) => {
      return state.bookmarkedSensors.filter(b => !b.deleted);
    },
    userType: (state, getters, rootState) => {
      return rootState.auth.user?.user?.Info?.user_type;
    },
  },
  actions: {
    async upsertAttribute({ rootState, state, commit }, payload) {
      let url = rootState.settings.api_base + state.url_upsert_attribute;

      if (
        payload.attribute_value == null ||
        payload.attribute_info_id == null ||
        payload.owner_id == null
      ) {
        // toast.error('Invalid attribute data. Please try again.');
        toast.error('You may not have permission to save this change.');
        return;
      }

      try {
        let rs = await axios.post(url, {
          attribute_value: payload.attribute_value,
          attribute_info_id: payload.attribute_info_id,
          owner_id: payload.owner_id,
        });
        // console.log('upsertAttribute', rs);
        commit(
          "apgList/setList",
          { listType: payload.listType, data: rs.data },
          { root: true }
        );
        toast.success(`Update Saved!`);
      } catch (err) {
        console.error("Failed to update attribute ", err);
        toast.error("Unable to update attribute. Please try again later.");
      }
    },

    async findAll({ rootState, state, commit }, payload) {
      const url = rootState.settings.api_base + state.url_list;
      const listType = typeof payload === 'string' ? payload : payload?.listType || 'user';
      // Note: Some roles (e.g., customer) may not have permission to list users.
      try {
        const rs = await axios.get(url);
        commit('apgList/setList', { listType, data: rs.data }, { root: true });
      } catch (err) {
        console.error('Failed to load user list.', err);
        // Interceptor will show a permission toast for 401s; otherwise show a generic error here.
        if (!err?.response || err.response.status !== 401) {
          toast.error('Unable to load users. Please try again later.');
        }
      }
    },

    async findProfile({ rootState, state, commit }, payload) {
      let user_id = rootState.auth.user.user.Auth.system_user_id;
      let url = rootState.settings.api_base + state.url_profile + '/' + user_id;
      // console.log(url);

      try {
        let rs = await axios.get(url);
        // console.log('profile info is', rs);
        commit(
          "apgList/setList",
          { listType: payload.listType, data: rs.data.profileAttrs },
          { root: true }
        );
      } catch (err) {
        console.error('Failed to load user profile info: ', err);
      }
    },

    async findOne({ rootState, state, commit }, payload) {
      // clear old attributes
      rootState.apgList.List.user_attribute = false;

      let user_id = rootState.apgList.ids['user'].user_id;
      let requester_id = rootState.auth.user.user.Auth.system_user_id;
      let url = rootState.settings.api_base + state.url_user + '/' + user_id + '/' + requester_id;
      // console.log(url)

      try {
        let rs = await axios.get(url);
        // console.log('user info is', rs)
        commit('apgList/setList', {listType: payload.listType ,data: rs.data.Attributes}, {root: true})
        commit('status_bar/setStatus', rs.data.status,{ root: true })
      } catch (err) {
        console.error("Failed to load user info.  Error was ");
        console.error({ err });
      }
    },
    async findAllForGroup ({ rootState, state, commit }, payload) {
      let url = rootState.settings.api_base + state.url_users_for_group + '/' + rootState.apgList.ids['group'].group_id;

      try {
        let rs = await axios.get(url);
        // console.log('found user list for group', rs.data)
        commit('apgList/setLists', {listType: payload.listType ,data: rs.data}, {root: true})
      } catch (err) {
        console.error('Failed to load user list for group.  Error was ',err);
      }
    },
    async removeGroupFromUser({ commit, rootState, state }, payload) {
      let url =
        rootState.settings.api_base +
        state.url_remove_user_from_group +
        rootState.apgList.ids["user"].user_id +
        "/" +
        rootState.apgList.ids[payload.listType].group_id;
      // console.log('removing user url is',url)
      // console.log('payload is ',payload)
      try {
        let rs = await axios.delete(url);
        // console.log('rs is :',rs);
        commit('apgList/setLists', {listType: payload.listType ,data: rs.data}, {root: true});
        toast.success('Removed user from group!');
      } catch(err) {
        console.error('Failed to remove user from group.  Error was ');
        console.error({ err });
        toast.error('Unable to remove user from group. Please try again later.');
      }
    },
    async addUsersToGroup({ state, commit, dispatch, rootState }, payload) {
      let checked = rootState.apgList.checked[payload.listType];
      if (!checked || checked.length === 0) {
        return;
      }

      let user_list = checked.join('-');
      let user_str = checked.length > 1 ? "users" : "user";
      let url = rootState.settings.api_base + state.url_add_users_to_group + rootState.apgList.ids['group'].group_id +
        "/" + user_list;
      try {
        await axios.put(url, {});
        commit('apgList/clearChecked', payload, {root: true});
        dispatch('findAllForGroup', payload);
        toast.success(`Added ${user_str} to group!`);
      } catch (err) {
        toast.error(`Unable to add ${user_str} to group. Please try again later.`);
      }
    },
    async sendTestNotification({ state, rootState }, payload) {
      let url =
        rootState.settings.api_base +
        state.url_profile +
        "/" +
        state.url_notification_test +
        payload.test_type +
        "/" +
        payload.user_id;
      //console.log('sendTestNotification url ', url);
      let res;
      try {
        res = await axios.put(url, {});
      } catch (err) {
        console.error('sendTestNotification', err);
      }
      return res;
    },

    async getLastReadings({ state, commit, rootState }) {
      // Check cache first
      const now = Date.now();
      const cacheKey = 'lastReadings';

      if (
        state.cache[cacheKey] &&
        now - state.cache.lastCacheTime[cacheKey] < state.cacheTimeout
      ) {
        state.lastReadings = state.cache[cacheKey];
        return;
      }

      commit('setLastReadingsLoading', true);
      let current_user_id = rootState.auth.user.user.Auth.system_user_id;
      let url = rootState.settings.api_base + 'user/last_readings/' + current_user_id;
      try {
        let res = await axios.get(url);
        state.lastReadings = res.data;
        // Cache the result
        state.cache[cacheKey] = res.data;
        state.cache.lastCacheTime[cacheKey] = now;
      } catch (err) {
        console.error('getLastReadings error:', err);
        toast.error('Unable to fetch last readings. Please try again later.');
      }
      commit('setLastReadingsLoading', false);
    },

    async fetchBookmarkedReadings({ commit, rootState }) {
      commit('setBookmarkedReadingsLoading', true);
      let url = rootState.settings.api_base + "bookmarks";
      try {
        let res = await axios.get(url);
        commit("setBookmarks", res.data);
      } catch (e) {
        console.error('fetchBookmarkedReadings error:', e);
        toast.error('Unable to fetch bookmarked readings. Please try again later.');
      }
      commit('setBookmarkedReadingsLoading', false);
    },

    async fetchCommunicationCounts({rootState, state}) {
      // Check cache first
      const now = Date.now();
      const cacheKey = 'communicationCounts';

      if (
        state.cache[cacheKey] &&
        now - state.cache.lastCacheTime[cacheKey] < state.cacheTimeout
      ) {
        state.communicationCounts = state.cache[cacheKey];
        return;
      }

      let current_user_id = rootState.auth.user.user.Auth.system_user_id;
      let url =
        rootState.settings.api_base + "user/commUsage/" + current_user_id;
      try {
        let rs = await axios.get(url);
        state.communicationCounts = rs.data;
        // Cache the result
        state.cache[cacheKey] = rs.data;
        state.cache.lastCacheTime[cacheKey] = now;
      } catch (err) {
        toast.error('Failed to load communication counts');
        console.error(`Failed to load my gateway list for me.  Error was ${err}`);
      }
    },

    async sendInvite({ rootState }, payload) {
      let current_user_id = rootState.auth.user.user.Auth.system_user_id;
      let url =
        rootState.settings.api_base + "user/invite/" + current_user_id;
      // console.log('sendInvite: url', url);
      let res;
      try {
        res = await axios.post(url, payload.data);
        if (res.data.status.code === 200) {
          toast.success(res.data.status.result);
        } else {
          toast.error(res.data.status.result);
        }
      } catch (err) {
        console.error('users store sendInvite', err);
        toast.error('Unable to send invite. Please try again later.');
      }
      return res;
    },

    async clickBookmark({ state, commit, dispatch }, sensorId) {
      const bookmark = getBookmarkedSensor(state, sensorId);
      if (!bookmark || bookmark.deleted) {
        commit('addBookmark', sensorId);
        await dispatch("saveBookmarkedSensors");
        return true;
      } else {
        commit('deleteBookmark', sensorId);
        await dispatch("saveBookmarkedSensors");
        return false;
      }
    },

    async getBookmarkedSensors({ dispatch }) {
      // Redirecting to the same consolidated fetch logic
      await dispatch('fetchBookmarkedReadings');
    },

    async saveBookmarkedSensors({ state, rootState }) {
      let url = rootState.settings.api_base + "bookmarks";
      try {
        // Send normalized bookmarks to ensure server gets correct field names
        const payload = state.bookmarkedSensors.map(b => ({
          sensor_id: b.sensor_id,
          deleted: b.deleted
        }));
        
        let res = await axios.post(url, { bookmarks: payload });
        if (res?.status !== 200) {
          toast.error("Error saving bookmarks");
        } else {
          // Invalidate cache
          state.cache.bookmarkedReadings = null;
          state.cache.lastCacheTime.bookmarkedReadings = 0;
          toast.success("Bookmarks updated successfully");
        }
      } catch (err) {
        toast.error("Error saving bookmarks");
        console.error("saveBookmarkedSensors error:", err);
      }
    },

    // Action to clear cache manually
    clearCache({state}) {
      state.cache.bookmarkedReadings = null;
      state.cache.lastReadings = null;
      state.cache.communicationCounts = null;
      state.cache.lastCacheTime.bookmarkedReadings = 0;
      state.cache.lastCacheTime.lastReadings = 0;
      state.cache.lastCacheTime.communicationCounts = 0;
    },
  },
};
